import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { DropdownSelect } from "../../components/forms/dropdown-select/dropdown-select";
import { MultiSelect, TextTypeInput, ToggleButton } from "../../components/forms";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { LeadLink } from "../../models/interfaces/lead-link";
import { DropdownSelectOption } from "../../models/interfaces/dropdown-select-option";
import { DatePicker } from "../../components/forms/date-picker";
import { Loader } from "../../components/loader/loader";
import { DateTime } from "luxon";
import { useStore } from "../../store/useStore";
import { enqueueSnackbar } from "notistack";
import { GroupOptions } from "../../models/interfaces/group-options";
import { DeeplinkStatus } from "../../models/enumerations/deeplink-status";
import LeadLinksService from "../../utilities/services/lead-links-service";
import { Button } from "../../components/button/button";
import { useTranslation } from 'react-i18next';
import { PageTabs } from "../../components/page-tabs/page-tabs";
import { Tabs } from "../../models/enumerations/tabs";
import AssessmentResultsHeaderStyling from "../../components/assessment-results-header-styling/assessment-results-header-styling";
import HomePageStyling from "../../models/interfaces/home-page-styling";
import { LinkType } from "../../models/enumerations/link-type";
import { FirebaseUtils } from "../../utilities/firebase-utils";
import { createLogger } from "../../utilities/logger";

const log = createLogger("LeadLinkAddEditPage");

const LEAD_CAPTURE_LINK_TYPE = "leadCapture";

interface LeadLinksAddEditPageParams {
    id: string;
}

interface AddEditFormData {
    name: string;
    group?: GroupOptions;
    tags?: DropdownSelectOption[];
    startDate?: string;
    endDate?: string;
    active: boolean;
    videoUrl?: string;
    webhookUrl?: string;
    orgDefaultContentConfig?: {
        groupNames?: string[];
        tagNames?: string[];
        linkNames?: string[];
    }
}

const LeadLinkAddEditPage: React.FC<LeadLinksAddEditPageParams> =
    (props: LeadLinksAddEditPageParams) => {
        const leadCaptureOrgId = process.env.REACT_APP_LEAD_CAPTURE_ORG_ID;
        const { id } = useParams<LeadLinksAddEditPageParams>();
        const { state } = useAuthState();
        const history = useHistory();
        const [isLoading, setIsLoading] = useState<boolean>(true);
        const [fetchedLeadLink, setFetchedLeadLink] = useState<LeadLink | null>(null);
        const [homePageStyling, setHomePageStyling] = useState<HomePageStyling>();
        const [fileToUpload, setFileToUpload] = useState<File>();
        const { t } = useTranslation();

        if (!state.user || !state.user?.isSuperAdmin) {
            throw new Error("You must be a super admin");
        }

        const methods = useForm<AddEditFormData>();

        const {
            getGroupsDropDownSelectOptionsByOrg,
            groupsDropdownSelectOption,
            getTagsDropDownSelectOptions,
            tagsDropdownSelectOptions,
            getTagById,
        } = useStore();

        const {
            register,
            handleSubmit,
            formState: { errors },
            setValue,
            reset,
            control,
            setError
        } = methods;

        useEffect(() => {
            const loadDependencies = async () => {
                if (leadCaptureOrgId) {
                    await getGroupsDropDownSelectOptionsByOrg(leadCaptureOrgId);
                    await getTagsDropDownSelectOptions(leadCaptureOrgId);
                }
            };

            const loadLeadLink = async () => {
                const leadLink = await LeadLinksService.get(id);

                if (!leadLink) {
                    return;
                }

                setFetchedLeadLink(leadLink);
                setHomePageStyling(leadLink.homePageStyling);

                let formData: AddEditFormData = {
                    name: leadLink.name,
                    active: leadLink.status === DeeplinkStatus.Active,
                    videoUrl: leadLink.videoUrl,
                    webhookUrl: leadLink.webhookUrl,
                    orgDefaultContentConfig: leadLink.orgDefaultContentConfig,
                };

                formData = {
                    ...formData, group: leadLink.group,
                }

                if (leadLink.tags) {
                    formData.tags = await Promise.all(leadLink.tags
                        .map(async (tag) => {
                            try {
                                return {
                                    label: tag.name,
                                    value: tag.id
                                } as DropdownSelectOption;
                            } catch (error: any) {
                                throw error;
                            }
                        }));
                }

                reset({
                    name: formData.name,
                    group: formData.group,
                    active: formData.active,
                    tags: formData.tags,
                    startDate: leadLink.startDate,
                    endDate: leadLink.endDate,
                    videoUrl: leadLink.videoUrl,
                    webhookUrl: leadLink.webhookUrl,
                    orgDefaultContentConfig: leadLink.orgDefaultContentConfig,
                });

                setIsLoading(false);
            };

            loadDependencies();

            if (id) {
                loadLeadLink();
            } else {
                reset({
                    active: true
                });
                setIsLoading(false);
            }
        }, [getGroupsDropDownSelectOptionsByOrg, getTagsDropDownSelectOptions, id, reset, getTagById, setValue, leadCaptureOrgId]);

        const isFormValid = (formData: AddEditFormData) => {
            if (!formData.startDate && !formData.endDate) {
                return true;
            }

            const start = DateTime.fromISO(formData.startDate!);
            const end = DateTime.fromISO(formData.endDate!);

            if (end < start) {
                setError("endDate",
                    {
                        type: "custom",
                        message: t('deeplinks.edit.err_endBeforeStart')
                    });
                return false;
            }

            return true;
        }

        const onSubmit = async (data: any) => {
            setIsLoading(true);

            // typecasting for use
            const formData = data as AddEditFormData;

            if (!isFormValid(formData)) {
                setIsLoading(false);
                return;
            }

            var entity = {
                name: formData.name,
                tags: [],
                status: formData.active ? DeeplinkStatus.Active : DeeplinkStatus.Inactive,
                organizationId: leadCaptureOrgId
            } as LeadLink;

            if (formData.tags) {
                formData.tags.forEach((tag) => {
                    entity?.tags?.push({
                        id: tag.value,
                        name: tag.label
                    })
                });
            }

            entity.group = formData.group;
            entity.startDate = formData.startDate;
            entity.endDate = formData.endDate;
            entity.videoUrl = formData.videoUrl;
            entity.webhookUrl = formData.webhookUrl;
            entity.orgDefaultContentConfig = formData.orgDefaultContentConfig;
            entity.homePageStyling = homePageStyling;

            if (id) {
                entity.id = id;

                try {
                    await LeadLinksService.update(entity);
                } catch (err) {
                    log.error('Failed to update the lead link', {
                        exception: err,
                        linkId: entity.id,
                    });
                }
            } else {
                try {
                    entity.clicksTotalCount = 0;
                    entity.leadsTotalCount = 0;
                    entity.starterTotalCount = 0;
                    entity.businessTotalCount = 0;
                    entity.enterpriseTotalCount = 0;

                    const createdLeadLink = await LeadLinksService.add(entity);
                    const baseUrl = process.env.REACT_APP_SYMMIO_PORTAL_URL;

                    if (baseUrl && createdLeadLink.id) {
                        createdLeadLink.url = `${baseUrl}/link-account-creation/${createdLeadLink.id}/${LEAD_CAPTURE_LINK_TYPE}`;

                        if (fileToUpload && createdLeadLink.id) {
                            await FirebaseUtils.handleCompanyLogoUpload(fileToUpload, createdLeadLink.id, LinkType.LeadCapture, state.organization)
                                .then((imageUrl) => {
                                    createdLeadLink.homePageStyling = {
                                        ...createdLeadLink.homePageStyling,
                                        logoUrl: imageUrl
                                    };
                                })
                                .catch((error) => {
                                    log.error("Error uploading file", {
                                        exception: error,
                                        linkId: createdLeadLink.id,
                                    });
                                });
                        }

                        await LeadLinksService.update(createdLeadLink);
                    }

                } catch (err: any) {
                    log.error('Failed to create a lead link', {
                        exception: err,
                    });
                    enqueueSnackbar(err);
                }
            }
            id ? enqueueSnackbar(t('deeplinks.edit.linkUpdated'), { variant: "toast", width: "450px" }) : enqueueSnackbar(t('deeplinks.edit.linkCreated'), { variant: "toast", width: "450px" });
            history.goBack();
        };

        const linkAddEditComponent = () => (
            <div className="grid grid-cols-12 gap-8">
                <div className="col-span-12 max-w-screen-md">
                    <Loader isVisible={isLoading} />
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                <TextTypeInput id="name"
                                    className="col-span-1"
                                    label={t('deeplinks.list.linkName')}
                                    registerHook={register}
                                    registerOptions={{ required: t('deeplinks.edit.val_linkName') }}
                                    errorState={errors.name} />
                            </div>
                            <DropdownSelect id="group"
                                label={t('deeplinks.list.group')}
                                optionsArray={groupsDropdownSelectOption}
                                control={control}
                                errorState={errors.group} />
                            <MultiSelect id="tags"
                                label={t('reports.fieldTags')}
                                control={control}
                                optionsArray={tagsDropdownSelectOptions}
                                setValue={setValue}
                                errorState={errors.tags} />
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                <TextTypeInput id="videoUrl"
                                    className="col-span-1"
                                    label={t('deeplinks.edit.videoURL')}
                                    registerHook={register}
                                    errorState={errors.videoUrl} />
                            </div>
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                                <TextTypeInput id="webhookUrl"
                                    className="col-span-1"
                                    label={t('deeplinks.edit.webhookUrl')}
                                    registerHook={register}
                                    errorState={errors.webhookUrl} />
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <DatePicker id="startDate"
                                    className="col-span-1"
                                    label={t('deeplinks.edit.startDate')}
                                    control={control} />
                                <DatePicker id="endDate"
                                    className="col-span-1"
                                    label={t('deeplinks.edit.endDate')}
                                    control={control} />
                            </div>
                            {id &&
                                <ToggleButton id="active" register={register} mainLabelText={t('deeplinks.edit.active')} />
                            }
                            <div className={'actions-container'} style={{ gap: "50px", marginTop: "16px" }}>
                                <Button
                                    type="link"
                                    buttonText={t('buttons.btn_cancel')}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        history.goBack();
                                    }} />
                                <Button
                                    buttonType="submit"
                                    buttonText={id ? t('buttons.btn_saveChanges') : t('deeplinks.edit.addLink')} />
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        );

        return (
            <div>
                <h2>{(!id) ? t('sitemap.addLeadCapture') : t('sitemap.editLeadCapture')}</h2>

                <PageTabs components={[
                    {
                        tab: Tabs.Link, tabLabel: t('deeplinks.edit.tabs.link'), component: linkAddEditComponent,
                    },
                    {
                        tab: Tabs.AssessmentResultsStyling,
                        tabLabel: t('deeplinks.edit.tabs.customBranding'),
                        component: AssessmentResultsHeaderStyling,
                        props: {
                            onSave: async (homePageStyling: HomePageStyling, file?: File) => {
                                if (fetchedLeadLink && fetchedLeadLink.id) {
                                    if (file) {
                                        await FirebaseUtils.handleCompanyLogoUpload(file, fetchedLeadLink.id, LinkType.LeadCapture, state.organization)
                                            .then((imageUrl) => {
                                                homePageStyling.logoUrl = imageUrl;
                                            })
                                            .catch((error) => {
                                                log.error("Error uploading file", {
                                                    exception: error,
                                                    linkId: fetchedLeadLink.id,
                                                });
                                            });
                                    }

                                    fetchedLeadLink.homePageStyling = homePageStyling;

                                    await LeadLinksService.update(fetchedLeadLink).then((updatedLeadLink) => {
                                        setFetchedLeadLink({ ...updatedLeadLink });

                                        enqueueSnackbar(t('deeplinks.edit.headerStylingUpdated'), {
                                            variant: "toast",
                                            width: "450px"
                                        });
                                    }).catch((error) => {
                                        log.error(t('deeplinks.edit.err_headerStylingUpdateFail'), {
                                            exception: error,
                                            linkId: fetchedLeadLink.id,
                                        });
                                    });
                                }
                                else {
                                    setHomePageStyling(homePageStyling);

                                    if (file) {
                                        setFileToUpload(file);
                                    }

                                    enqueueSnackbar(t('deeplinks.edit.headerStylingUpdated'), {
                                        variant: "toast",
                                        width: "450px"
                                    });
                                }
                            },
                            homePageStyling: homePageStyling || fetchedLeadLink?.homePageStyling,
                        }
                    },
                ]} initialTab={Tabs.Link} highlightLineId="leadCaptureLinkTabsHighlightLine" />
            </div>
        );
    };

export default LeadLinkAddEditPage;
